import type { ReactNode } from "react";

// Lightweight wrapper around the existing table pattern. Adds:
// - sticky header
// - zebra rows
// - hover state
// - right-aligned numeric columns via the `align` prop on columns
//
// Pages can still use raw <table> when they need a non-standard cell layout,
// but the common case (5–10 column sortable readout) goes through here.

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  align?: "left" | "right" | "center";
  width?: string;
  render: (row: T, idx: number) => ReactNode;
  mono?: boolean;
}

interface Props<T> {
  rows: T[];
  columns: DataTableColumn<T>[];
  rowKey: (row: T, idx: number) => string;
  rowClassName?: (row: T, idx: number) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  dense?: boolean;
}

export default function DataTable<T>({
  rows,
  columns,
  rowKey,
  rowClassName,
  onRowClick,
  empty,
  dense = false,
}: Props<T>) {
  if (rows.length === 0 && empty !== undefined) {
    return <div>{empty}</div>;
  }
  const padY = dense ? "py-1.5" : "py-2.5";
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-muted bg-surface-2/60">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                style={c.width ? { width: c.width } : undefined}
                className={`${padY} px-3 font-medium ${
                  c.align === "right"
                    ? "text-right"
                    : c.align === "center"
                      ? "text-center"
                      : "text-left"
                }`}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const extra = rowClassName?.(row, idx) ?? "";
            return (
              <tr
                key={rowKey(row, idx)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`border-t border-border/60 ${
                  onRowClick ? "cursor-pointer" : ""
                } hover:bg-surface-2/60 ${extra}`}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`${padY} px-3 ${
                      c.align === "right"
                        ? "text-right"
                        : c.align === "center"
                          ? "text-center"
                          : "text-left"
                    } ${c.mono ? "font-mono" : ""}`}
                  >
                    {c.render(row, idx)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
